<script context="module">
  import { block } from 'marked/src/rules'

  // use regex for hr (---) on multiline string
  // that's actually /^ {0,3}((?:- *){3,}|(?:_ *){3,}|(?:\* *){3,})(?:\n+|$)/m
  const regexHr = new RegExp(block.hr.source, block.hr.flags + "m");
  const regexH1H2H3 = /^ {0,3}(#{1,3})(?=\s|$)(.*)(?:\n+|$)/m
  
  // checks if a markdown string is a heading.
  // returns an object
  const heading = string => {
    // console.log(block.heading, string.match(block.heading))
    const match = string.match(block.heading)
    //console.log(match)
    return {
      true: match !== null && match[0] === match.input, // if this is a heading
      content: match !== null && match[2],              // content of the heading
      depth: match !== null && match[1].length || 0     // depth (h1->1, h2->2, ...)
    }
  }

  // splits a markdown string by "---" (hr)
  // indicates the start of a new card
  // returns an array of strings
  export const splitIntoItems = source => { 
    const parts = source.split(regexHr).filter( (s,i) => i % 2 === 0)
    return parts
  }

  export const startsWithH1 = source => splitIntoItems(source)?.[0]?.heading?.depth === 1

</script>

<script>

  /*************************************
   * 
   * This component implements the "page style" markdown. specs are roughly as follows:
   * 
   *   --- (hr) starts a new card
   *   #   (h1) when it is the only element of a card creates a huge page header, no card
   *   ##  (h2) when it is the only element of a card creates a green "heading style" card
   * 
   *   other elements render inside the card as regular styled markdown
   * 
  *************************************/

  import Card from '/components/Atoms/Card'
  import CardHeadline from '/components/Widgets/CardHeadline'
  import PageHeader from '/components/Atoms/PageHeader'
  import MarkdownBlock from '/components/Atoms/MarkdownBlock'
  import Image from '/Atoms/Image'
  import Video from '/Atoms/Video'

  export let blocks = []

  let cards = []

  $: {
    // STEP 1: split into cards
    cards = []

    // initialize first card
    let currentCard = []

    // go through blocks
    for (let i = 0; i < blocks.length; i++) {
      let currentBlock = blocks[i]
      // text blocks
      if (currentBlock.textBlock) {
        // split text blocks at card breaks
        let currentParts = splitIntoItems(currentBlock.textBlock)
        for (let j = 0; j < currentParts.length; j++) {
          let currentPart = currentParts[j]
          if (j === 0) {
            // first one continues current card
            //console.log("text block continues current card")
            currentCard.push({
              type: "text",
              source: currentPart
            })
          } else {
            // following ones each start a new card
            //console.log("start card #"+(cards.length+2), currentPart)
            cards.push([...currentCard])
            currentCard = [{
              type: "text",
              source: currentPart
            }]
          }
        }
      }
      // image blocks continue the current card
      if (currentBlock.imageBlock) {
        //console.log("image block continues current card")
        currentCard.push({
          type: "image", 
          ...currentBlock.imageBlock[0]
        }) // take only the first image for now!
      }
      // video blocks continue the current card
      if (currentBlock.typeHandle === "videoBlock") {
        //console.log("image block continues current card")
        currentCard.push({
          type: "video", 
          ...currentBlock
        })
      }
    }

    // push last card
    cards.push([...currentCard])

    //console.log("cards", cards)

    // STEP 2: analyze cards

    cards = cards.map( items => {
      if (items.length === 1) {
        const item = items[0]
        if (item.type == "text") {
          const h = heading(item.source)
          if (h.true) {
            return {
              cardType: "heading",
              heading: h
            }
          }
        }
      }

      return {
        cardType: "multi",
        items
      }

    })

    //console.log("cards", cards)

  }

</script>

<div class="markdown">
  {#each cards as card}
    <div class="card" 
      class:regular={!card.cardType == "heading"}
      class:h1={card.cardType=="heading" && card.heading.depth === 1} 
      class:hGreen={card.cardType == "heading" && card.heading.depth !== 1}
    >
      {#if card.cardType == "heading" }
        {#if card.heading.depth === 1}
          <PageHeader>
            {card.heading.content}
          </PageHeader>
        {:else}
          <CardHeadline> 
            {card.heading.content}
          </CardHeadline>
        {/if}
      {:else}
        <Card paddingStyle="headline">  
          {#each card.items as item}
            {#if item.type == "text"}
              <div class="textblock">
                <MarkdownBlock source={item.source} />
              </div>
            {/if}
            {#if item.type == "image"}
              <div class="imageblock">
                <Image 
                  src={item.url}
                  srcset={`${item.url} 1x, ${item.url2x} 2x`}
                  caption={item.imageCaption}
                  credits={item.credits}
                />
              </div>
            {/if}
            {#if item.type == "video"}
              <div class="videoblock">
                <Video vimeoUrl={item.vimeoUrl}/>
              </div>
            {/if}
          {/each}
        </Card>
      {/if}
    </div>
  {/each}
</div>

<style lang="scss">

  .card {
    margin-bottom: 20px;
    &.h1 {
      margin-bottom: 0px;
    }
  }

  .card.regular + .card.hGreen {
    margin-top: 40px;
  }

  .imageblock + .textblock,
  .textblock + .imageblock,
  .textblock + .videoblock,
  .imageblock + .videoblock {
    margin-top: 30px;
  }

  .videoblock {
    padding-bottom: 1em;
    margin: 0 60px;
    @include media-small {
      margin: 0;
    }
  }

</style>